import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'

import { useActivePatient } from '@context/ActivePatientContext'
import {
  useUpdatePatient,
  useSignIdentityCard,
  useUploadPatientPhoto,
} from '@hooks/usePatients'
import { useToast } from '@hooks/use-toast'
import { cn, calculateAge, getInitials } from '@lib/utils'
import { getApiErrorMessage } from '@services/api'
import { EditInsuranceContactsDialog } from '@components/patients/EditInsuranceContactsDialog'
import { PatientIdentificationCard } from '@/components/patient/PatientIdentificationCard'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const genderLabels: Record<string, string> = {
  male: 'Masculino',
  female: 'Femenino',
  non_binary: 'No binario',
  prefer_not_to_say: 'Prefiere no decir',
  other: 'Otro',
}

const SELECTABLE_GENDERS = ['male', 'female'] as const

const maritalLabels: Record<string, string> = {
  single: 'Soltero/a',
  married: 'Casado/a',
  divorced: 'Divorciado/a',
  widowed: 'Viudo/a',
  separated: 'Separado/a',
  partnered: 'Unión libre',
}

const coverageLabels: Record<string, string> = {
  private: 'Particular (privado)',
  insurance: 'Seguro de gastos médicos',
  institutional: 'Institucional (IMSS / ISSSTE)',
}

const datosSchema = z.object({
  first_name: z.string().min(1, 'Obligatorio').max(100),
  middle_name: z.string().max(100).optional(),
  last_name: z.string().min(1, 'Obligatorio').max(100),
  date_of_birth: z.string().refine((d) => new Date(d) <= new Date(), {
    message: 'La fecha no puede ser futura',
  }),
  gender: z.enum(['male', 'female', 'non_binary', 'prefer_not_to_say', 'other']),
  place_of_birth: z.string().max(150).optional(),
  nationality: z.string().max(100).optional(),
  curp: z.string().max(18).optional(),
  marital_status: z.string().optional(),
  education_level: z.string().max(100).optional(),
  occupation: z.string().max(100).optional(),
  religion: z.string().max(100).optional(),
  native_language: z.string().max(100).optional(),
  blood_type: z.string().optional(),
  phone_primary: z.string().max(20).optional(),
  phone_secondary: z.string().max(20).optional(),
  email: z.string().email('Correo inválido').optional().or(z.literal('')),
  address_line1: z.string().max(255).optional(),
  address_line2: z.string().max(255).optional(),
  city: z.string().max(100).optional(),
  state: z.string().max(100).optional(),
  postal_code: z.string().max(20).optional(),
  country: z.string().max(100).optional(),
  coverage_type: z.string().optional(),
  referring_physician: z.string().max(200).optional(),
  is_dependent: z.boolean().optional(),
  guardian_name: z.string().max(200).optional(),
  guardian_relationship: z.string().max(100).optional(),
  guardian_phone: z.string().max(20).optional(),
  guardian_id_document: z.string().max(100).optional(),
})

type DatosFormData = z.infer<typeof datosSchema>

// ── Field styling ────────────────────────────────────────────────────────────
// Filled fields read as plain dark text (no box); empty fields show an italic
// gray "Pendiente / Opcional" placeholder. A faint surface appears on hover/focus
// to signal editability — no static borders around fields (white space instead).
const fieldInputClass = cn(
  'w-full rounded-md border border-transparent bg-transparent px-1.5 py-1 text-sm font-medium text-ml-on-surface outline-none transition-colors',
  'placeholder:font-normal placeholder:italic placeholder:text-ml-on-surface-variant/45',
  'hover:bg-ml-surface-container-high/50 focus:border-ml-outline-variant/50 focus:bg-ml-surface-container-high',
)
const labelClass = 'mb-0.5 block text-[11px] font-normal text-ml-on-surface-variant'

function selectClassFor(hasValue: boolean) {
  return cn(
    'w-full cursor-pointer appearance-none rounded-md border border-transparent bg-transparent px-1.5 py-1 text-sm outline-none transition-colors',
    'hover:bg-ml-surface-container-high/50 focus:border-ml-outline-variant/50 focus:bg-ml-surface-container-high',
    hasValue ? 'font-medium text-ml-on-surface' : 'italic text-ml-on-surface-variant/45',
  )
}

function EMPTY_DEFAULTS(): DatosFormData {
  return {
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: '',
    gender: 'male',
    place_of_birth: '',
    nationality: '',
    curp: '',
    marital_status: '',
    education_level: '',
    occupation: '',
    religion: '',
    native_language: '',
    blood_type: '',
    phone_primary: '',
    phone_secondary: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    coverage_type: '',
    referring_physician: '',
    is_dependent: false,
    guardian_name: '',
    guardian_relationship: '',
    guardian_phone: '',
    guardian_id_document: '',
  }
}

export function DatosPacienteTab() {
  const { activePatientData, setActivePatient } = useActivePatient()
  const updatePatient = useUpdatePatient()
  const { mutate: signCard, isPending: isSigning } = useSignIdentityCard()
  const uploadPhoto = useUploadPatientPhoto()
  const { toast } = useToast()
  const [editEmergencyContactsOpen, setEditEmergencyContactsOpen] = useState(false)
  const [signOpen, setSignOpen] = useState(false)
  const photoInputRef = useRef<HTMLInputElement>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<DatosFormData>({
    resolver: zodResolver(datosSchema),
    defaultValues: EMPTY_DEFAULTS(),
  })

  useEffect(() => {
    if (activePatientData) {
      reset({
        ...EMPTY_DEFAULTS(),
        first_name: activePatientData.first_name ?? '',
        middle_name: activePatientData.middle_name ?? '',
        last_name: activePatientData.last_name ?? '',
        date_of_birth: activePatientData.date_of_birth ?? '',
        gender: activePatientData.gender ?? 'male',
        place_of_birth: activePatientData.place_of_birth ?? '',
        nationality: activePatientData.nationality ?? '',
        curp: activePatientData.curp ?? '',
        marital_status: activePatientData.marital_status ?? '',
        education_level: activePatientData.education_level ?? '',
        occupation: activePatientData.occupation ?? '',
        religion: activePatientData.religion ?? '',
        native_language: activePatientData.native_language ?? '',
        blood_type: activePatientData.blood_type ?? '',
        phone_primary: activePatientData.phone_primary ?? '',
        phone_secondary: activePatientData.phone_secondary ?? '',
        email: activePatientData.email ?? '',
        address_line1: activePatientData.address_line1 ?? '',
        address_line2: activePatientData.address_line2 ?? '',
        city: activePatientData.city ?? '',
        state: activePatientData.state ?? '',
        postal_code: activePatientData.postal_code ?? '',
        country: activePatientData.country ?? '',
        coverage_type: activePatientData.coverage_type ?? '',
        referring_physician: activePatientData.referring_physician ?? '',
        is_dependent: activePatientData.is_dependent ?? false,
        guardian_name: activePatientData.guardian_name ?? '',
        guardian_relationship: activePatientData.guardian_relationship ?? '',
        guardian_phone: activePatientData.guardian_phone ?? '',
        guardian_id_document: activePatientData.guardian_id_document ?? '',
      })
    }
  }, [activePatientData, reset])

  const values = watch()

  if (!activePatientData) return null

  // Signed → the locked, read-only identification card with addendum action.
  if (activePatientData.identity_signed_at) {
    return (
      <div className="animate-fade-in space-y-6">
        <PatientIdentificationCard patient={activePatientData} showAddendumAction />
      </div>
    )
  }

  const dobForAge = values.date_of_birth || activePatientData.date_of_birth
  const age =
    dobForAge && !Number.isNaN(new Date(dobForAge).getTime()) && new Date(dobForAge) <= new Date()
      ? calculateAge(dobForAge)
      : null
  const isMinor = age !== null && age < 18
  const showGuardian = !!values.is_dependent || isMinor

  const allergies = (activePatientData.allergies ?? []).filter((a) => a?.name)

  const onSubmit = async (data: DatosFormData) => {
    try {
      const updated = await updatePatient.mutateAsync({ id: activePatientData.id, data })
      toast({ title: 'Cambios guardados', description: 'La información del paciente se actualizó correctamente.' })
      if (updated) setActivePatient(updated)
    } catch (err) {
      console.error('Failed to update patient:', err)
      toast({
        title: 'No pudimos guardar los cambios',
        description: getApiErrorMessage(err as any, 'Inténtalo nuevamente. Si el problema continúa, verifica que la API esté disponible.'),
        variant: 'destructive',
      })
    }
  }

  const onPhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      const res = await uploadPhoto.mutateAsync({ patientId: activePatientData.id, file })
      setActivePatient({ ...activePatientData, photo_url: res.photo_url })
      toast({ title: 'Fotografía actualizada' })
    } catch (err) {
      toast({
        title: 'No se pudo subir la fotografía',
        description: getApiErrorMessage(err as any, 'Intenta con otra imagen (JPG/PNG, máx. 5 MB).'),
        variant: 'destructive',
      })
    } finally {
      if (photoInputRef.current) photoInputRef.current.value = ''
    }
  }

  const handleSign = () => {
    signCard(activePatientData.id, {
      onSuccess: (updated) => {
        setActivePatient(updated)
        setSignOpen(false)
        toast({ title: 'Ficha firmada y cerrada', description: 'La ficha de identificación quedó bloqueada (solo lectura).' })
      },
      onError: () => {
        toast({ title: 'No se pudo firmar', description: 'Ocurrió un error al cerrar la ficha de identificación.', variant: 'destructive' })
      },
    })
  }

  const emergencyContacts = activePatientData.emergency_contacts ?? []
  const sortedEmergencyContacts = [...emergencyContacts].sort(
    (a, b) => Number(b.is_primary) - Number(a.is_primary)
  )
  const primaryInsurance =
    activePatientData.insurance_info?.find((i) => i.is_primary) ??
    activePatientData.insurance_info?.[0]
  const primaryEmergency = sortedEmergencyContacts[0]

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="animate-fade-in space-y-3">
        {/* ── 1 · Patient header — sober, no fill/gradient ───────────────── */}
        <div
          className="rounded-lg border border-ml-outline-variant/25 bg-ml-surface px-5 py-4"
          title="Editable hasta firmar · NOM-004-SSA3-2012"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={onPhotoChange}
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadPhoto.isPending}
                title="Cambiar fotografía"
                className="relative shrink-0 rounded-full transition hover:opacity-90"
              >
                {activePatientData.photo_url ? (
                  <img
                    src={activePatientData.photo_url}
                    alt={activePatientData.full_name}
                    className="h-11 w-11 rounded-full object-cover ring-2 ring-ml-outline-variant/25"
                  />
                ) : (
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ml-surface-container-high text-base font-bold text-ml-on-surface ring-2 ring-ml-outline-variant/20">
                    {getInitials(activePatientData.first_name, activePatientData.last_name)}
                  </span>
                )}
                {uploadPhoto.isPending && (
                  <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/30">
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  </span>
                )}
              </button>
              <div className="min-w-0">
                <h2 className="truncate text-2xl font-bold leading-tight text-ml-on-surface">
                  {activePatientData.full_name}
                </h2>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs leading-tight text-ml-on-surface-variant">
                  <span className="font-mono">Exp. {activePatientData.mrn}</span>
                  {age !== null && <span>· {age} años</span>}
                  <span className="text-ml-on-surface-variant/50">· NOM-004</span>
                </p>
              </div>
            </div>
            <span className="inline-flex shrink-0 items-center rounded border border-ml-outline-variant/50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-ml-on-surface-variant">
              Abierta · sin firmar
            </span>
          </div>
        </div>

        {/* ── 2 · Allergy alert — the single chromatic exception (muted red) */}
        {allergies.length > 0 && (
          <div className="border-l-2 border-red-800/70 bg-red-50/60 px-4 py-2.5">
            <p className="min-w-0 text-sm leading-snug text-red-900">
              <span className="mr-2 text-[10px] font-bold uppercase tracking-[0.15em] text-red-800">
                Alergias
              </span>
              {allergies.map((a, i) => (
                <span key={`${a.name}-${i}`}>
                  {i > 0 && ', '}
                  <span className="font-semibold">{a.name}</span>
                  {(a.reaction || a.severity) && (
                    <span className="text-red-900/70">
                      {' '}({[a.reaction, a.severity].filter(Boolean).join(' · ')})
                    </span>
                  )}
                </span>
              ))}
            </p>
          </div>
        )}

        {/* ── 3 · Document body — two continuous columns, no cards ─────────
            A typeset clinical record: each category is a heading with a thin
            rule and a 2–3 field grid beneath, separated by white space rather
            than boxes. Left column = personal/demographic, right = contact,
            address, insurance, guardian. Grayscale throughout. */}
        <div className="grid grid-cols-1 gap-x-12 gap-y-9 px-1 pt-2 lg:grid-cols-2">
          {/* Left column — personal & demographic */}
          <div className="space-y-9">
            <Section title="Identidad">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                <div>
                  <FieldLabel required empty={!values.first_name}>Nombre(s)</FieldLabel>
                  <input {...register('first_name')} className={fieldInputClass} placeholder="Pendiente" />
                  <ErrText msg={errors.first_name?.message} />
                </div>
                <div>
                  <FieldLabel>Segundo nombre</FieldLabel>
                  <input {...register('middle_name')} className={fieldInputClass} placeholder="Opcional" />
                </div>
                <div>
                  <FieldLabel required empty={!values.last_name}>Apellidos</FieldLabel>
                  <input {...register('last_name')} className={fieldInputClass} placeholder="Pendiente" />
                  <ErrText msg={errors.last_name?.message} />
                </div>
              </div>
            </Section>

            <Section title="Nacimiento y demografía">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                <div>
                  <FieldLabel required empty={!values.date_of_birth}>
                    Fecha de nacimiento
                    {age !== null && (
                      <span className="ml-1.5 text-ml-on-surface-variant/70">· {age} años</span>
                    )}
                  </FieldLabel>
                  <input
                    type="date"
                    {...register('date_of_birth')}
                    className={cn(fieldInputClass, !values.date_of_birth && 'text-ml-on-surface-variant/50')}
                  />
                  <ErrText msg={errors.date_of_birth?.message} />
                </div>
                <div>
                  <FieldLabel>Sexo</FieldLabel>
                  <select {...register('gender')} className={selectClassFor(!!values.gender)}>
                    {SELECTABLE_GENDERS.map((value) => (
                      <option key={value} value={value}>{genderLabels[value]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <FieldLabel>Estado civil</FieldLabel>
                  {isMinor ? (
                    <p className="px-1.5 py-1 text-sm italic text-ml-on-surface-variant/55">No aplica — menor</p>
                  ) : (
                    <select {...register('marital_status')} className={selectClassFor(!!values.marital_status)}>
                      <option value="">Pendiente</option>
                      {Object.entries(maritalLabels).map(([value, lbl]) => (
                        <option key={value} value={value}>{lbl}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <FieldLabel>CURP</FieldLabel>
                  <input {...register('curp')} className={cn(fieldInputClass, 'font-mono uppercase')} placeholder="Pendiente" />
                  <ErrText msg={errors.curp?.message} />
                </div>
                <div>
                  <FieldLabel>Lugar de nacimiento</FieldLabel>
                  <input {...register('place_of_birth')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
                <div>
                  <FieldLabel>Nacionalidad</FieldLabel>
                  <input {...register('nationality')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
              </div>
            </Section>

            <Section title="Perfil">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                <div>
                  <FieldLabel>Escolaridad</FieldLabel>
                  <input {...register('education_level')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
                <div>
                  <FieldLabel>Ocupación</FieldLabel>
                  <input {...register('occupation')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
                <div>
                  <FieldLabel>Grupo sanguíneo</FieldLabel>
                  <select {...register('blood_type')} className={selectClassFor(!!values.blood_type)}>
                    <option value="">Pendiente</option>
                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((bt) => (
                      <option key={bt} value={bt}>{bt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <FieldLabel>Religión</FieldLabel>
                  <input {...register('religion')} className={fieldInputClass} placeholder="Opcional" />
                </div>
                <div>
                  <FieldLabel>Lengua materna</FieldLabel>
                  <input {...register('native_language')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
              </div>
            </Section>
          </div>

          {/* Right column — contact, address, insurance, guardian */}
          <div className="space-y-9">
            <Section title="Teléfono y correo">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <div>
                  <FieldLabel>Teléfono principal</FieldLabel>
                  <input {...register('phone_primary')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
                <div>
                  <FieldLabel>Teléfono secundario</FieldLabel>
                  <input {...register('phone_secondary')} className={fieldInputClass} placeholder="Opcional" />
                </div>
                <div className="col-span-2">
                  <FieldLabel>Correo electrónico</FieldLabel>
                  <input {...register('email')} type="email" className={fieldInputClass} placeholder="Opcional" />
                  <ErrText msg={errors.email?.message} />
                </div>
              </div>
            </Section>

            <Section title="Domicilio">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <FieldLabel>Calle y número</FieldLabel>
                  <input {...register('address_line1')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
                <div>
                  <FieldLabel>Colonia / unidad</FieldLabel>
                  <input {...register('address_line2')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
                <div>
                  <FieldLabel>Ciudad</FieldLabel>
                  <input {...register('city')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
                <div>
                  <FieldLabel>Estado</FieldLabel>
                  <input {...register('state')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
                <div>
                  <FieldLabel>Código postal</FieldLabel>
                  <input {...register('postal_code')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
                <div>
                  <FieldLabel>País</FieldLabel>
                  <input {...register('country')} className={fieldInputClass} placeholder="Pendiente" />
                </div>
              </div>
            </Section>

            <Section
              title="Representante legal / tutor"
              action={
                <div className="flex items-center gap-3 text-[11px] font-medium">
                  <button
                    type="button"
                    onClick={() => setValue('is_dependent', false, { shouldDirty: true })}
                    disabled={isMinor}
                    className={cn(
                      'transition-colors',
                      !showGuardian ? 'font-semibold text-ml-on-surface underline underline-offset-2' : 'text-ml-on-surface-variant/55 hover:text-ml-on-surface',
                      isMinor && 'cursor-not-allowed opacity-40',
                    )}
                  >
                    No aplica
                  </button>
                  <span className="text-ml-outline-variant/50">·</span>
                  <button
                    type="button"
                    onClick={() => setValue('is_dependent', true, { shouldDirty: true })}
                    className={cn(
                      'transition-colors',
                      showGuardian ? 'font-semibold text-ml-on-surface underline underline-offset-2' : 'text-ml-on-surface-variant/55 hover:text-ml-on-surface',
                    )}
                  >
                    Dependiente
                  </button>
                </div>
              }
            >
              {showGuardian ? (
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <FieldLabel>Nombre completo</FieldLabel>
                    <input {...register('guardian_name')} className={fieldInputClass} placeholder="Pendiente" />
                  </div>
                  <div>
                    <FieldLabel>Parentesco</FieldLabel>
                    <input {...register('guardian_relationship')} className={fieldInputClass} placeholder="Pendiente" />
                  </div>
                  <div>
                    <FieldLabel>Teléfono</FieldLabel>
                    <input {...register('guardian_phone')} className={fieldInputClass} placeholder="Pendiente" />
                  </div>
                  <div>
                    <FieldLabel>Identificación</FieldLabel>
                    <input {...register('guardian_id_document')} className={cn(fieldInputClass, 'font-mono')} placeholder="Pendiente" />
                  </div>
                </div>
              ) : (
                <p className="px-1.5 text-sm italic text-ml-on-surface-variant/50">
                  No aplica — sin tutor o representante legal.
                </p>
              )}
            </Section>
          </div>

          {/* Insurance & referral — full-width band so all four fields read
              cleanly across the page instead of clipping in a narrow column. */}
          <div className="lg:col-span-2">
            <Section
              title="Aseguramiento y referencia"
              action={
                <button
                  type="button"
                  onClick={() => setEditEmergencyContactsOpen(true)}
                  className="text-[11px] font-medium text-ml-on-surface-variant underline-offset-2 hover:text-ml-on-surface hover:underline"
                >
                  Editar seguros
                </button>
              }
            >
              <div className="grid grid-cols-1 gap-x-10 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <FieldLabel>Tipo de cobertura</FieldLabel>
                  <select {...register('coverage_type')} className={selectClassFor(!!values.coverage_type)}>
                    <option value="">Pendiente</option>
                    {Object.entries(coverageLabels).map(([value, lbl]) => (
                      <option key={value} value={value}>{lbl}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <FieldLabel>Médico que refiere</FieldLabel>
                  <input {...register('referring_physician')} className={fieldInputClass} placeholder="Opcional" />
                </div>
                <DisplayField label="Aseguradora" value={primaryInsurance?.provider} />
                <DisplayField label="Núm. de póliza" value={primaryInsurance?.policy_number} mono />
              </div>
            </Section>
          </div>

          {/* Emergency contact — full-width band across both columns so the
              record reads cleanly and is never clipped in a narrow column. */}
          <div className="lg:col-span-2">
            <Section
              title="Contacto de emergencia"
              action={
                <button
                  type="button"
                  onClick={() => setEditEmergencyContactsOpen(true)}
                  className="text-[11px] font-medium text-ml-on-surface-variant underline-offset-2 hover:text-ml-on-surface hover:underline"
                >
                  Editar o agregar
                </button>
              }
            >
              {primaryEmergency ? (
                <div className="grid grid-cols-1 gap-x-10 gap-y-3 sm:grid-cols-3">
                  <div>
                    <FieldLabel>Nombre</FieldLabel>
                    <p className="px-1.5 py-1 text-sm font-medium text-ml-on-surface">{primaryEmergency.name}</p>
                  </div>
                  <div>
                    <FieldLabel>Parentesco</FieldLabel>
                    <p
                      className={cn(
                        'px-1.5 py-1 text-sm',
                        primaryEmergency.relationship ? 'font-medium text-ml-on-surface' : 'italic text-ml-on-surface-variant/50',
                      )}
                    >
                      {primaryEmergency.relationship || 'Pendiente'}
                    </p>
                  </div>
                  <div>
                    <FieldLabel>Teléfono</FieldLabel>
                    <p className="px-1.5 py-1 text-sm font-medium text-ml-on-surface">{primaryEmergency.phone}</p>
                  </div>
                </div>
              ) : (
                <p className="px-1.5 py-1 text-sm italic text-ml-on-surface-variant/50">
                  Pendiente — sin contacto de emergencia
                </p>
              )}
            </Section>
          </div>
        </div>

        {/* Footer — save + sign-and-close */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-ml-outline-variant/15 pt-4 pb-6">
          <p className="max-w-md text-[11px] leading-relaxed text-ml-on-surface-variant/70">
            Al firmar, la ficha se cierra y queda{' '}
            <span className="font-semibold">bloqueada y de solo lectura</span> a su nombre y cédula
            profesional. Después solo podrá corregirse mediante una nota de aclaración. Guarda los
            cambios antes de firmar.
          </p>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={!isDirty || isSubmitting}
              className={cn(
                'rounded-md bg-ml-on-surface px-8 py-3 font-semibold text-ml-surface transition-all',
                isDirty ? 'opacity-100 hover:opacity-90' : 'cursor-not-allowed opacity-40'
              )}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </span>
              ) : (
                'Guardar cambios'
              )}
            </button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setSignOpen(true)}
              disabled={isDirty}
              title={isDirty ? 'Guarda los cambios antes de firmar' : undefined}
            >
              Firmar y cerrar ficha
            </Button>
          </div>
        </div>
      </form>

      <EditInsuranceContactsDialog
        patient={activePatientData}
        open={editEmergencyContactsOpen}
        onOpenChange={setEditEmergencyContactsOpen}
        onUpdated={setActivePatient}
      />

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Firmar y cerrar la ficha de identificación</DialogTitle>
            <DialogDescription>
              La ficha quedará firmada a su nombre y cédula profesional, con la fecha y hora
              actuales, y se bloqueará como solo lectura. Después solo podrá corregirse mediante una
              nota de aclaración. ¿Desea continuar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)} disabled={isSigning}>
              Cancelar
            </Button>
            <Button onClick={handleSign} disabled={isSigning}>
              {isSigning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Firmar y cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ── Presentational helpers ───────────────────────────────────────────────────

/** A document section: a small letter-spaced uppercase heading underlined by a
 *  thin gray rule (printed-record style), with its fields below. No box, fill,
 *  shadow or color — hierarchy comes from type, the rule and white space. */
function Section({
  title,
  action,
  children,
}: {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <section>
      <div className="mb-3 flex items-end justify-between gap-3 border-b border-ml-outline-variant/30 pb-1.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ml-on-surface">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  )
}

function FieldLabel({
  children,
  required = false,
  empty = false,
}: {
  children: React.ReactNode
  required?: boolean
  empty?: boolean
}) {
  return (
    <label className="mb-0.5 flex items-center gap-1 text-[11px] font-normal text-ml-on-surface-variant">
      {children}
      {required && empty && (
        <span title="Campo obligatorio pendiente" className="text-ml-on-surface-variant/70">
          *
        </span>
      )}
    </label>
  )
}

/** Read-only value in the same label-on-top / value-below rhythm. */
function DisplayField({
  label,
  value,
  mono = false,
}: {
  label: string
  value?: string | null
  mono?: boolean
}) {
  const empty = !value
  return (
    <div>
      <p className={labelClass}>{label}</p>
      <p
        className={cn(
          'px-1.5 py-1 text-sm',
          empty ? 'italic text-ml-on-surface-variant/50' : 'font-medium text-ml-on-surface',
          mono && !empty && 'font-mono'
        )}
      >
        {empty ? 'Opcional — no proporcionado' : value}
      </p>
    </div>
  )
}

function ErrText({ msg }: { msg?: string }) {
  if (!msg) return null
  return <p className="mt-1 text-xs text-red-800">{msg}</p>
}
